// The third-party creative review link lives in the URL FRAGMENT, never the path
// or the query, so the credential reaches no server log, no Referer header and
// no Pages request. One parser and one builder, shared by the page and its test,
// because a link misread here routes a reviewer into the wrong flow.
//
// Shapes:
//
//   #p=<share token>[&c=<creative id>]          a review link
//   #v=<challenge>&p=<share token>[&c=<id>]     the emailed confirmation link
//   #v=<challenge>[&c=<id>]                     a confirmation emailed before
//                                               links carried `p`
//   #<share token>                              a pre-verification single-
//                                               creative link, still honoured
//
// `c` is optional everywhere: a link names one creative, and `c` survives only
// for the multi-item packages minted before that. A malformed `c` is dropped,
// never fatal — it can only point at something to scroll to.

/** 64 hex: the share-token shape the worker validates. */
export const SHARE_TOKEN_RE = /^[0-9a-fA-F]{64}$/;
/** 32 bytes base64url without padding: a challenge, and a device id. */
export const CHALLENGE_RE = /^[A-Za-z0-9_-]{43}$/;
/** `CreativeId`'s slug shape (`shared/src/ids.rs`). */
export const CREATIVE_ID_RE = /^[a-z0-9_-]{1,64}$/;

export type ReviewFragment =
  | { kind: 'package'; token: string; creativeId: string | null }
  | { kind: 'verify'; challenge: string; token: string | null; creativeId: string | null }
  | { kind: 'legacy'; token: string }
  | { kind: 'invalid' };

const INVALID: ReviewFragment = { kind: 'invalid' };

/**
 * Read a fragment (with or without its leading `#`). Unknown keys are ignored;
 * a repeated or malformed `p` or `v` makes the whole link invalid, because
 * guessing which copy was meant could open the wrong package.
 */
export function parseFragment(hash: string): ReviewFragment {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw.includes('=')) {
    return SHARE_TOKEN_RE.test(raw) ? { kind: 'legacy', token: raw } : INVALID;
  }
  const fields = new Map<string, string>();
  for (const pair of raw.split('&')) {
    const at = pair.indexOf('=');
    if (at <= 0) return INVALID;
    const key = pair.slice(0, at);
    if (fields.has(key)) return INVALID;
    fields.set(key, pair.slice(at + 1));
  }
  const token = fields.get('p');
  const challenge = fields.get('v');
  const rawCreative = fields.get('c');
  const creativeId = rawCreative !== undefined && CREATIVE_ID_RE.test(rawCreative) ? rawCreative : null;
  if (token !== undefined && !SHARE_TOKEN_RE.test(token)) return INVALID;
  if (challenge !== undefined) {
    if (!CHALLENGE_RE.test(challenge)) return INVALID;
    return { kind: 'verify', challenge, token: token ?? null, creativeId };
  }
  if (token === undefined) return INVALID;
  return { kind: 'package', token, creativeId };
}

/**
 * The fragment for a state, `#` included. Order is fixed (`v`, `p`, `c`) so a
 * rewrite of an unchanged state produces an identical string.
 */
export function buildFragment(parts: {
  challenge?: string | null;
  token?: string | null;
  creativeId?: string | null;
}): string {
  const pairs: string[] = [];
  if (parts.challenge) pairs.push(`v=${parts.challenge}`);
  if (parts.token) pairs.push(`p=${parts.token}`);
  if (parts.creativeId) pairs.push(`c=${parts.creativeId}`);
  return `#${pairs.join('&')}`;
}
